'use client';

import { cn } from '@utils/cn';
import type { PromptBarMode } from './usePromptBarMode';
import { usePromptBarActions } from './usePromptBarActions';

interface MissionChipsRowProps {
  locale: Locale;
  mode: PromptBarMode;
  isAuthenticated: boolean;
  className?: string;
}

/**
 * The mission list rendered as a centered row of chips below the prompt bar — same items
 * as the `+` popup. The current mission's chip looks identical to the rest but clicking it
 * does nothing (handleClick no-ops on the selected item). Wraps onto multiple rows on
 * narrow viewports.
 */
export const MissionChipsRow = ({ locale, mode, isAuthenticated, className }: MissionChipsRowProps) => {
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, mode, isAuthenticated);
  const items = [...candidateItems, ...companyItems];

  return (
    <div className={cn('flex w-full flex-wrap items-center justify-center gap-1.5', className)}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          aria-current={item.selected ? 'page' : undefined}
          onClick={() => handleClick(item)}
          className={cn(
            'rounded-full border border-input bg-surface px-2.5 py-1 text-xs text-ink shadow-sm transition-colors lg:text-sm',
            item.selected ? 'font-semibold' : 'cursor-pointer hover:bg-surface-100',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
